import React, {useEffect, useRef} from "react";
import "./Drawer.css";
import { XMarkIcon } from '@heroicons/react/24/outline';

interface DrawerProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
}

const Drawer: React.FC<DrawerProps> = ({
    isOpen,
    onClose,
    title,
    children
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    // Handle escape key press
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    // Handle backdrop click
    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    // Handle touch events for swipe down to close
    const handleTouchStart = useRef<number>(0);
    const handleTouchMove = useRef<number>(0);

    const onTouchStart = (event: React.TouchEvent) => {
        handleTouchStart.current = event.touches[0].clientY;
    };

    const onTouchMove = (event: React.TouchEvent) => {
        handleTouchMove.current = event.touches[0].clientY;
    };

    const onTouchEnd = () => {
        const touchDiff = handleTouchMove.current - handleTouchStart.current;
        // If swiped down more than 100px, close modal
        if (touchDiff > 100) {
            onClose();
        }
    };

    return (
        <div 
            className={`Drawer-backdrop ${isOpen ? 'open' : ''}`}
            onClick={handleBackdropClick}
        >
            <div 
                ref={modalRef}
                className={`Drawer ${isOpen ? 'open' : ''}`}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                {/* Handle bar for visual indication and touch interaction */}
                <div className="Drawer-handle"></div>
                
                {/* Header with title and close button */}
                <div className="Drawer-header">
                    {title && <h2 className="Drawer-title">{title}</h2>}
                    <button 
                        className="Drawer-close"
                        onClick={onClose}
                        aria-label="Fermer le tiroir"
                    >
                        <XMarkIcon className="Drawer-close-icon" />
                    </button>
                </div>

                {/* Content area */}
                <div className="Drawer-content">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Drawer;
